import {
  Box,
  Icon
} from "@chakra-ui/react";
import { Storage } from "aws-amplify";
import { differenceInSeconds } from "date-fns";
import React, {
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import {
  BsPauseCircleFill,
  BsPlayCircleFill
} from "react-icons/bs";
import styled, { css } from "styled-components";

import {
  Conversation,
  Participant
} from "src/api";
import { AudioMedia } from "src/api/audio-media";
import { Avatar } from "src/components/avatar";
import { Text } from "src/components/text";
import { theme } from "src/components/theme";

import { UserAudio } from "./AudioBox";
import {
  AudioContainer,
  PageContainer
} from "./VoiceChat";

interface VoicePlaybackProps {
  audioMedia: AudioMedia[]
  conversation: Conversation
}

interface ParticipantAudioStream {
  id: string;
  context: AudioContext;
  analyser: AnalyserNode;
  participant: Participant;
  source: MediaElementAudioSourceNode;
  primary?: boolean;
}

export const VoicePlayback: React.FC<VoicePlaybackProps> = props => {
  const [ playbackMedia, setPlaybackMedia ] = useState<AudioMedia[]>([]);
  const [ streams, setStreamsToPlay ] = useState<ParticipantAudioStream[]>([]);
  const totalDuration = differenceInSeconds(new Date(props.conversation.endTime), new Date(props.conversation.startTime));
  const [ timeElapsed, setTimeElapsed ] = useState(0);
  const [ isPaused, setIsPaused ] = useState(false);
  const interval = useRef<NodeJS.Timeout>();

  /**
   * Fetch the signed audio files from S3 and add to state
   */
  const getPlaybackSources = useCallback(async () => {
    const mediaSources: AudioMedia[] = [];

    for await (const media of props.audioMedia) {
      // Break down the URL so we can sign the URL properly with Storage.get():
      // URL:         "/protected/eu-west-1:abcd-1234-abcd-1234/abc-123.mp4"
      // Identity ID: "eu-west-1:abcd-1234-abcd-1234"
      const userIdentityId = media.fileUrl.split("/protected/")[ 1 ].split("/")[ 0 ];
      // File name:   "abc-123.mp4"
      const fileKey = media.fileUrl.match(/[^/]+$/);

      if (fileKey && userIdentityId) {
        const file = await Storage.get(fileKey?.toString(), {
          level: "protected",
          identityId: userIdentityId.toString()
        });

        mediaSources.push({
          ...media,
          fileUrl: file
        });
      }
    }

    setPlaybackMedia(mediaSources);
  }, [ props.audioMedia ]);

  /**
   * Setup audio files
   *
   * Using primaryUserId to decide which file gets played without delay
   * Create an audio player with the signed audio file from S3
   * Add to streamsToPlay state
   * including the participant info and analyser for the audio visualiser
   */
  const playAudio = useCallback((primaryUserId: string) => {
    const streamsToPlay: ParticipantAudioStream[] = [];
    const primaryStartTime = playbackMedia.find(media => media.userId === primaryUserId)?.startTime || props.conversation.startTime;
    const alreadyElapsedSessionTime = differenceInSeconds(new Date(primaryStartTime), new Date(props.conversation.startTime));

    // If the primary user joined late, then set the new start time
    setTimeElapsed(alreadyElapsedSessionTime);

    playbackMedia.map(media => {
      const isPrimary = media.userId === primaryUserId;
      const participant = props.conversation.participants?.find(participant => participant.user._id === media.userId);
      const audioPlayer = new Audio(media.fileUrl);

      audioPlayer.crossOrigin = "anonymous";

      // This is the delay before the user started speaking
      // This is the added delay to simulate latency
      const latencyDelay = props.conversation.latencySeconds * 1000;

      if (isPrimary) {
        // Play without any delay
        audioPlayer.play();
      } else {
        // Play what the primary user would have heard
        let playDelay = latencyDelay;
        const startDelay = differenceInSeconds(new Date(primaryStartTime), new Date(media.startTime));

        if (startDelay > 0) {
          audioPlayer.currentTime = startDelay;
        } else {
          playDelay = playDelay + Math.abs(startDelay) * 1000;
        }

        setTimeout(() => {
          audioPlayer.play();
        }, playDelay);
      }

      // Create an audio context with an analyser for audio visualisation
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const source = ctx.createMediaElementSource(audioPlayer);
      const analyser = ctx.createAnalyser();

      // Connect the analyser and output the sound
      source.connect(analyser);
      source.connect(ctx.destination);

      if (participant) {
        streamsToPlay.push({
          id: media._id,
          analyser,
          source,
          context: ctx,
          participant,
          primary: isPrimary
        });
      }
    });
    setStreamsToPlay(streamsToPlay);
  }, [ playbackMedia, props.conversation ]);

  const pause = () => {
    streams.map(stream => {
      stream.context.suspend();
      setIsPaused(true);
    });
  };

  const play = () => {
    streams.map(stream => {
      stream.context.resume();
      setIsPaused(false);
    });
  };

  useEffect(() => {
    // Fetch S3 signed URLs for all audio files
    getPlaybackSources();

    return () => {
      // On unmount disconnect all of the audio sources so that it stops playing
      streams.map(stream => {
        stream.source.disconnect();
      });
    };
  }, [ getPlaybackSources, streams ]);

  useEffect(() => {
    // When we start playing,
    if (streams.length) {
      // Add a second to the progress bar
      interval.current = setInterval(() => {
        if (!isPaused) {
          setTimeElapsed(current => current + 1);
        }
      }, 1000);
    }

    return () => {
      if (interval.current) {
        // On unmout clear the interval
        clearInterval(interval.current);
      }
    };
  }, [ isPaused, streams.length ]);

  useEffect(() => {
    if (timeElapsed === totalDuration) {
      interval.current && clearInterval(interval.current);
    }
  }, [ timeElapsed, totalDuration ]);

  return (
    <PageContainer>

      {!streams.length && playbackMedia.map(media => {
        const participant = props.conversation.participants?.find(participant => participant.user._id === media.userId);

        return (
          <ButtonWrapper
            onClick={() => playAudio(media.userId)}
            key={media.userId}
          >
            {participant?.user.name && (
              <Box
                style={{
                  padding: "2rem",
                  display: "flex",
                  alignItems: "center",
                  flexDirection: "row"
                }}
              >
                <Text
                  style={{ paddingRight: "0.75rem" }}
                >
                  Listen back as
                </Text>

                <Avatar
                  name={participant?.user.name}
                />

              </Box>

            )}

          </ButtonWrapper>
        );
      })}

      {streams.length > 0 && (
        <Box
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column"
          }}
        >
          <Box
            style={{
              marginTop: "1rem",
              display: "flex",
              flexDirection: "row",
              alignItems: "center"
            }}
          >
            <TimerWrapper>
              <ProgressBar
                style={{ width: `${timeElapsed / totalDuration * 100}%` }}
              />

            </TimerWrapper>

            {isPaused ? (
              <Icon
                w={18}
                h={18}
                ml={2}
                onClick={play}
                cursor="pointer"
                color={theme.colors.main}
                as={BsPlayCircleFill}
              />
            ) : (
              <Icon
                w={18}
                h={18}
                ml={2}
                onClick={pause}
                cursor="pointer"
                color={theme.colors.main}
                as={BsPauseCircleFill}
              />
            )}
          </Box>

          <AudioContainer>
            {streams.map(stream => (
              <UserAudio
                key={stream.participant.user._id}
                name={stream.participant.user.name}
                analyser={stream.analyser}
                delayMs={0}
                byCurrentUser={stream.primary}
              />
            ))}
          </AudioContainer>
        </Box>
      )}

    </PageContainer>

  );
};

const TimerWrapper = styled(Box)`
  width: 100%;
  height: 10px;
  border-radius: 20px;
  background: #fafafa;
  position: relative;
`;

const ProgressBar = styled(Box)`
  ${({ theme }) => css`
    position: absolute; 
    height: 100%;
    transition: all ease-in-out 0.2s;
    border-radius: 20px;
    z-index: 2;
    border: 1px solid white;
    max-width: 100%;
    background: ${theme.colors.main};
  `}
`;

const ButtonWrapper = styled(Box)`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    flex-direction: column;
    margin-top: 2rem;
    margin-bottom: 2rem;
    cursor: pointer;
    border-radius: 20px;
    border: 2px solid ${theme.colors.main};
    transition: background-color ease-in-out 0.1s;

    &:hover {
      background: #Fafafa;
    }
  `}
`;